/**
 * One window: traffic lights, draggable title bar, 8 resize handles, focus on
 * pointerdown. During a gesture we write transform/size STRAIGHT to the DOM
 * node (no React state per pointermove); the store gets one commit on release.
 */
import { useRef, type ReactNode } from "react";
import {
  clampBounds,
  closeWindow,
  commitBounds,
  focusWindow,
  minimizeWindow,
  toggleMaximize,
  type WindowState,
} from "./store";

type Edge = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
const EDGES: Edge[] = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
const MIN_W = 320;
const MIN_H = 220;

export function WindowFrame({
  win,
  zIndex,
  focused,
  children,
}: {
  win: WindowState;
  zIndex: number;
  focused: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // live bounds during a gesture — committed once on pointerup
  const live = useRef(win.bounds);
  // manual double-press detection: setPointerCapture retargets the browser's
  // derived dblclick to the capture element, so onDoubleClick on the title bar
  // never fires — we detect the second quick press ourselves.
  const lastPress = useRef(0);

  const apply = (b: typeof win.bounds) => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = `translate3d(${b.x}px, ${b.y}px, 0)`;
    el.style.width = `${b.w}px`;
    el.style.height = `${b.h}px`;
  };

  const startDrag = (e: React.PointerEvent) => {
    // traffic lights and other buttons in the bar must not start a drag; nor does a right or middle press
    if ((e.target as HTMLElement).closest("button") || e.button !== 0) return;
    // NO preventDefault here: cancelling pointerdown suppresses the derived
    // mouse events. Selection during drag is prevented by user-select:none.
    focusWindow(win.id);
    const now = performance.now();
    if (now - lastPress.current < 350) {
      lastPress.current = 0;
      toggleMaximize(win.id);
      return;
    }
    lastPress.current = now;
    const el = ref.current!;
    el.classList.add("dragging");
    el.setPointerCapture(e.pointerId);
    const from = { ...win.bounds };
    const ox = e.clientX;
    const oy = e.clientY;
    live.current = from;
    // A zero-movement "drag" (e.g. each half of a double-click) must not commit:
    // commitBounds clears `maximized`, which corrupts the dblclick-to-restore flow.
    let moved = false;

    const move = (ev: PointerEvent) => {
      moved = true;
      // a real drag is not the first half of a double-press
      lastPress.current = 0;
      live.current = clampBounds(
        { ...from, x: from.x + (ev.clientX - ox), y: from.y + (ev.clientY - oy) },
        window.innerWidth,
        window.innerHeight,
      );
      apply(live.current);
    };
    // The gesture ends on release, and also when the browser cancels it or takes the capture away (Safari does both),
    // so a window is never left half-dragged with listeners still attached.
    const up = () => {
      el.classList.remove("dragging");
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      el.removeEventListener("lostpointercapture", up);
      if (moved) commitBounds(win.id, live.current);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("lostpointercapture", up);
  };

  const startResize = (edge: Edge) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    focusWindow(win.id);
    const el = ref.current!;
    el.classList.add("dragging");
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const from = { ...win.bounds };
    const ox = e.clientX;
    const oy = e.clientY;
    live.current = from;
    let moved = false;

    const move = (ev: PointerEvent) => {
      moved = true;
      const dx = ev.clientX - ox;
      const dy = ev.clientY - oy;
      let { x, y, w, h } = from;
      if (edge.includes("e")) w = Math.max(MIN_W, from.w + dx);
      if (edge.includes("s")) h = Math.max(MIN_H, from.h + dy);
      if (edge.includes("w")) {
        w = Math.max(MIN_W, from.w - dx);
        x = from.x + (from.w - w);
      }
      if (edge.includes("n")) {
        h = Math.max(MIN_H, from.h - dy);
        y = from.y + (from.h - h);
      }
      live.current = clampBounds({ x, y, w, h }, window.innerWidth, window.innerHeight);
      apply(live.current);
    };
    const target = e.target as HTMLElement;
    const up = () => {
      el.classList.remove("dragging");
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", up);
      target.removeEventListener("pointercancel", up);
      target.removeEventListener("lostpointercapture", up);
      if (moved) commitBounds(win.id, live.current);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", up);
    target.addEventListener("pointercancel", up);
    target.addEventListener("lostpointercapture", up);
  };

  const { bounds } = win;
  return (
    <div
      ref={ref}
      className={
        "rdr-window" + (focused ? "" : " unfocused") + (win.minimized ? " minimized" : "")
      }
      style={{
        transform: `translate3d(${bounds.x}px, ${bounds.y}px, 0)`,
        width: bounds.w,
        height: bounds.h,
        zIndex,
      }}
      onPointerDown={() => focusWindow(win.id)}
      role="dialog"
      aria-label={win.title}
    >
      <div className="rdr-titlebar" onPointerDown={startDrag}>
        <div className="rdr-tl">
          <button type="button" className="close" aria-label="Close" onClick={() => closeWindow(win.id)}>
            <svg viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M1.5 1.5l5 5M6.5 1.5l-5 5" /></svg>
          </button>
          <button type="button" className="min" aria-label="Minimize" onClick={() => minimizeWindow(win.id)}>
            <svg viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M1.5 4h5" /></svg>
          </button>
          <button type="button" className="zoom" aria-label="Zoom" onClick={() => toggleMaximize(win.id)}>
            <svg viewBox="0 0 8 8" fill="currentColor"><path d="M1.7 4.9V1.7h3.2zM6.3 3.1v3.2H3.1z" /></svg>
          </button>
        </div>
        <span className="title">{win.title}</span>
        <span />
      </div>
      <div className="rdr-winbody">{children}</div>
      {!win.maximized && EDGES.map((edge) => (
        <div key={edge} className={`rdr-rs ${edge}`} onPointerDown={startResize(edge)} />
      ))}
    </div>
  );
}
